import { Component, ContentChildren, ElementRef, EventEmitter, forwardRef, Input, Output, QueryList, TemplateRef, ViewChild } from "@angular/core";
import { NzSelectComponent } from "ng-zorro-antd/select";
import { InputBoolean } from "ng-zorro-antd/core/util";
import { NzSafeAny } from "ng-zorro-antd/core/types";
import { WithConfig } from "ng-zorro-antd/core/config";
import { UiFilterOptionType, UiSelectItemInterface, UiSelectModeType, UiSelectOptionInterface, UiSelectSizeType } from "@webapp/ui/select/select.models";
import { uiSelectDefaultFilterOption } from "@webapp/ui/select/utils/select.utils";
import { NG_VALUE_ACCESSOR } from "@angular/forms";
import { CdkConnectedOverlay, CdkOverlayOrigin } from "@angular/cdk/overlay";
import { UiSelectTopControlComponent } from "@webapp/ui/select/components/select-top-control/select-top-control.component";
import { UiOptionGroupComponent } from "@webapp/ui/select/components/option-group/option-group.component";
import { UiOptionComponent } from "@webapp/ui/select/components/option/option.component";

@Component({
  selector: "ui-select",
  exportAs: "uiSelect",
  preserveWhitespaces: false,
  templateUrl: "select.component.html",
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UiSelectComponent),
      multi: true,
    },
  ],
})
export class UiSelectComponent extends NzSelectComponent {
  @Input("uiId") public nzId: string | null = null;
  @Input("uiSize") public nzSize: UiSelectSizeType = "default";
  @Input("uiOptionHeightPx") public nzOptionHeightPx = 32;
  @Input("uiOptionOverflowSize") public nzOptionOverflowSize = 8;
  @Input("uiDropdownClassName") public nzDropdownClassName: string | null = null;
  @Input("uiDropdownMatchSelectWidth") public nzDropdownMatchSelectWidth = true;
  @Input("uiDropdownStyle") public nzDropdownStyle: { [key: string]: string } | null = null;
  @Input("uiNotFoundContent") public nzNotFoundContent: string | TemplateRef<NzSafeAny> | undefined = undefined;
  @Input("uiPlaceHolder") public nzPlaceHolder: string | TemplateRef<NzSafeAny> | null = null;
  @Input("uiMaxTagCount") public nzMaxTagCount = Infinity;
  @Input("uiDropdownRender") public nzDropdownRender: TemplateRef<NzSafeAny> | null = null;
  @Input("uiCustomTemplate") public nzCustomTemplate: TemplateRef<{ $implicit: UiSelectItemInterface }> | null = null;
  @Input("uiSuffixIcon")
  @WithConfig<TemplateRef<NzSafeAny> | string | null>()
  public nzSuffixIcon: TemplateRef<NzSafeAny> | string | null = null;
  @Input("uiClearIcon") public nzClearIcon: TemplateRef<NzSafeAny> | null = null;
  @Input("uiRemoveIcon") public nzRemoveIcon: TemplateRef<NzSafeAny> | null = null;
  @Input("uiMenuItemSelectedIcon") public nzMenuItemSelectedIcon: TemplateRef<NzSafeAny> | null = null;
  @Input("uiTokenSeparators") public nzTokenSeparators: string[] = [];
  @Input("uiMaxTagPlaceholder") public nzMaxTagPlaceholder: TemplateRef<{ $implicit: NzSafeAny[] }> | null = null;
  @Input("uiMaxMultipleCount") public nzMaxMultipleCount = Infinity;
  @Input("uiMode") public nzMode: UiSelectModeType = "default";
  @Input("uiFilterOption") public nzFilterOption: UiFilterOptionType = uiSelectDefaultFilterOption;
  @Input("uiCompareWith") public compareWith: (o1: NzSafeAny, o2: NzSafeAny) => boolean = (o1: NzSafeAny, o2: NzSafeAny) => o1 === o2;
  @Input("uiAllowClear") @InputBoolean() public nzAllowClear = false;
  @Input("uiBorderless") @WithConfig<boolean>() @InputBoolean() public nzBorderless = false;
  @Input("uiShowSearch") @InputBoolean() public nzShowSearch = false;
  @Input("uiLoading") @InputBoolean() public nzLoading = false;
  @Input("uiAutoFocus") @InputBoolean() public nzAutoFocus = false;
  @Input("uiAutoClearSearchValue") @InputBoolean() public nzAutoClearSearchValue = true;
  @Input("uiServerSearch") @InputBoolean() public nzServerSearch = false;
  @Input("uiDisabled") @InputBoolean() public nzDisabled = false;
  @Input("uiOpen") @InputBoolean() public nzOpen = false;
  @Input("uiBackdrop") @WithConfig<boolean>() @InputBoolean() public nzBackdrop = false;
  @Input("uiOptions") public nzOptions: UiSelectOptionInterface[] = [];

  @Input("uiShowArrow")
  set selectShowArrow(value: boolean) {
    this.nzShowArrow = value;
  }
  get selectShowArrow(): boolean {
    return this.nzShowArrow;
  }

  @Output("uiOnSearch") public nzOnSearch = new EventEmitter<string>();
  @Output("uiScrollToBottom") public nzScrollToBottom = new EventEmitter<void>();
  @Output("uiOpenChange") public nzOpenChange = new EventEmitter<boolean>();
  @Output("uiBlur") public nzBlur = new EventEmitter<void>();
  @Output("uiFocus") public nzFocus = new EventEmitter<void>();

  @ViewChild(CdkOverlayOrigin, { static: true, read: ElementRef }) public originElement!: ElementRef;
  @ViewChild(CdkConnectedOverlay, { static: true }) public cdkConnectedOverlay!: CdkConnectedOverlay;
  @ViewChild(UiSelectTopControlComponent, { static: true }) public nzSelectTopControlComponent!: UiSelectTopControlComponent;
  @ContentChildren(UiOptionComponent, { descendants: true }) public listOfNzOptionComponent!: QueryList<UiOptionComponent>;
  @ContentChildren(UiOptionGroupComponent, { descendants: true })
  public listOfNzOptionGroupComponent!: QueryList<UiOptionGroupComponent>;
  @ViewChild(UiOptionGroupComponent, { static: true, read: ElementRef }) public nzOptionGroupComponentElement!: ElementRef;
  @ViewChild(UiSelectTopControlComponent, { static: true, read: ElementRef })
  public nzSelectTopControlComponentElement!: ElementRef;
}
